import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];
  /** @type {HTMLElement | null} */
  #desktopTrigger = null;
  /** @type {HTMLDetailsElement | null} */
  #desktopAccordion = null;
  #desktopAccordionWasOpen = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    document.addEventListener('click', this.#openDesktopProducts);
    document.addEventListener('keydown', this.#desktopSpace);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener('click', this.#openDesktopProducts);
    document.removeEventListener('keydown', this.#desktopSpace);
  }


  /** Open the existing mobile drawer from the desktop Products link. */
  #openDesktopProducts = (event) => {
    if (!(event.target instanceof Element) || !matchMedia('(min-width: 750px)').matches) return;
    const trigger = event.target.closest('[data-mc-desktop-products]');
    if (!(trigger instanceof HTMLElement) || !this.matches('.header__drawer--mobile')) return;
    if (!this.closest('header-component')?.contains(trigger)) return;
    event.preventDefault();
    this.#desktopTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    this.classList.add('mc-desktop-open');
    const accordion = this.querySelector('.menu-drawer__menu.has-submenu > li:first-child details');
    if (accordion instanceof HTMLDetailsElement && !this.#desktopAccordion) {
      this.#desktopAccordion = accordion;
      this.#desktopAccordionWasOpen = accordion.open;
      accordion.open = true;
    }
    const panel = this.querySelector('[data-header-drawer]');
    panel?.setAttribute('role', 'dialog');
    panel?.setAttribute('aria-modal', 'true');
    panel?.setAttribute('aria-label', trigger.textContent?.trim() || 'Menu');
    this.refs.details.open = true;
    this.open();
    requestAnimationFrame(() => {
      trapFocus(panel instanceof HTMLElement ? panel : this.refs.details);
      this.querySelector('.menu-drawer__close-button')?.focus();
    });
  };

  /** Match native button behavior for the link's button role. */
  #desktopSpace = (event) => {
    if (event.key !== ' ' || !(event.target instanceof HTMLElement)) return;
    if (!event.target.matches('[data-mc-desktop-products]') || !this.closest('header-component')?.contains(event.target)) return;
    if (!matchMedia('(min-width: 750px)').matches) return;
    event.preventDefault();
    event.target.click();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    if (this.classList.contains('mc-desktop-open')) { this.close(); return; }

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => {
        if (this.classList.contains('mc-desktop-open') && drawer instanceof HTMLElement) {
          trapFocus(drawer);
          drawer.querySelector('.menu-drawer__close-button')?.focus();
        } else {
          trapFocus(details);
        }
      }, { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          if (this.classList.contains('mc-desktop-open')) {
            this.classList.remove('mc-desktop-open');
            this.#desktopTrigger?.setAttribute('aria-expanded', 'false');
            if (this.#desktopAccordion) this.#desktopAccordion.open = this.#desktopAccordionWasOpen;
            this.#desktopAccordion = null;
            const panel = this.querySelector('[data-header-drawer]');
            panel?.removeAttribute('role');
            panel?.removeAttribute('aria-modal');
            panel?.removeAttribute('aria-label');
            if (matchMedia('(min-width: 750px)').matches) this.#desktopTrigger?.focus();
            this.#desktopTrigger = null;
          }
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
